// src/components/tenant/AdminUserCard.tsx
// ✅ UPDATED: Cognito-native card for displaying admin user in tenant details

import React, { useState } from 'react';
import { 
  User, 
  Mail, 
  Clock, 
  RotateCcw, 
  UserX, 
  CheckCircle, 
  AlertTriangle,
  Calendar,
  Shield,
  MoreVertical,
  Send,
  PowerOff,
  Power
} from 'lucide-react';
import { 
  TenantUser,
  userProvisioningAPI 
} from '../../services/userProvisioningAPI';

interface AdminUserCardProps {
  tenantId: string;
  adminUser?: TenantUser;
  onUpdate: () => void;
  onInviteNew: () => void;
}

const AdminUserCard: React.FC<AdminUserCardProps> = ({
  tenantId,
  adminUser,
  onUpdate,
  onInviteNew
}) => {
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getStatusInfo = () => {
    if (!adminUser) {
      return {
        status: 'NO_ADMIN',
        label: 'No Admin User',
        icon: UserX,
        color: 'text-gray-600',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200'
      };
    }

    const statusInfo = userProvisioningAPI.formatUserStatus(adminUser.status);
    
    if (adminUser.status === 'CONFIRMED') {
      return {
        status: 'ACTIVE',
        label: 'Active Admin',
        icon: CheckCircle,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      };
    }

    if (adminUser.status === 'FORCE_CHANGE_PASSWORD') {
      return {
        status: 'INVITED',
        label: statusInfo.label,
        icon: Clock,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200'
      };
    }

    return {
      status: 'INACTIVE',
      label: statusInfo.label,
      icon: AlertTriangle,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200'
    };
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  const handleResendInvitation = async () => {
    if (loading || !adminUser) return;

    setLoading(true);
    setError(null);

    try {
      const response = await userProvisioningAPI.resendInvitation(adminUser.userId, tenantId);
      
      if (response.success) {
        console.log('✅ Invitation resent successfully');
        onUpdate();
        setShowDropdown(false);
      } else {
        throw new Error(response.error?.message || 'Failed to resend invitation');
      }
    } catch (err: any) {
      console.error('❌ Error resending invitation:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisableUser = async () => {
    if (loading || !adminUser) return;

    const confirmMessage = 'Are you sure you want to disable this admin user? They will no longer be able to access the system.';
    if (!window.confirm(confirmMessage)) return;

    setLoading(true);
    setError(null);

    try {
      const response = await userProvisioningAPI.disableUser(adminUser.userId, tenantId);
      
      if (response.success) {
        console.log('✅ User disabled successfully');
        onUpdate();
        setShowDropdown(false);
      } else {
        throw new Error(response.error?.message || 'Failed to disable user');
      }
    } catch (err: any) {
      console.error('❌ Error disabling user:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEnableUser = async () => {
    if (loading || !adminUser) return;

    setLoading(true);
    setError(null);

    try {
      const response = await userProvisioningAPI.enableUser(adminUser.userId, tenantId);
      
      if (response.success) {
        console.log('✅ User enabled successfully');
        onUpdate();
        setShowDropdown(false);
      } else {
        throw new Error(response.error?.message || 'Failed to enable user');
      }
    } catch (err: any) {
      console.error('❌ Error enabling user:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canResendInvitation = adminUser && userProvisioningAPI.canResendInvitation(adminUser.status);

  return (
    <div className={`border rounded-lg p-4 ${statusInfo.bgColor} ${statusInfo.borderColor}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <StatusIcon className={`w-5 h-5 ${statusInfo.color} mr-2`} />
          <h4 className="text-sm font-medium text-gray-900">Administrator</h4>
        </div>
        
        <div className="flex items-center space-x-2">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${statusInfo.color} ${statusInfo.bgColor} ${statusInfo.borderColor}`}>
            {statusInfo.label}
          </span>
          
          {/* Actions Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              disabled={loading}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-md disabled:opacity-50"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            
            {showDropdown && (
              <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                <div className="py-1">
                  {statusInfo.status === 'NO_ADMIN' && (
                    <button
                      onClick={() => {
                        onInviteNew();
                        setShowDropdown(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Send className="w-4 h-4 inline mr-2" />
                      Invite Admin User
                    </button>
                  )}
                  
                  {canResendInvitation && (
                    <button
                      onClick={handleResendInvitation}
                      disabled={loading}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <RotateCcw className="w-4 h-4 inline mr-2" />
                      Resend Invitation
                    </button>
                  )}
                  
                  {adminUser && adminUser.enabled && (
                    <button
                      onClick={handleDisableUser}
                      disabled={loading}
                      className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      <PowerOff className="w-4 h-4 inline mr-2" />
                      Disable User
                    </button>
                  )}
                  
                  {adminUser && !adminUser.enabled && (
                    <button
                      onClick={handleEnableUser}
                      disabled={loading}
                      className="block w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-green-50 disabled:opacity-50"
                    >
                      <Power className="w-4 h-4 inline mr-2" />
                      Enable User
                    </button>
                  )}
                  
                  {statusInfo.status === 'NO_ADMIN' && (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      No actions available
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-md">
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      {/* Admin User Info */}
      {adminUser ? (
        <div className="space-y-2">
          <div className="flex items-center text-sm">
            <User className="w-4 h-4 text-gray-400 mr-2" />
            <span className="text-gray-900">
              {adminUser.firstName} {adminUser.lastName}
            </span>
          </div>
          
          <div className="flex items-center text-sm">
            <Mail className="w-4 h-4 text-gray-400 mr-2" />
            <span className="text-gray-700">{adminUser.email}</span>
          </div>
          
          <div className="flex items-center text-sm">
            <Shield className="w-4 h-4 text-gray-400 mr-2" />
            <span className="text-gray-700">{adminUser.role}</span>
          </div>
          
          <div className="flex items-center text-sm">
            <Calendar className="w-4 h-4 text-gray-400 mr-2" />
            <span className="text-gray-600">
              {adminUser.status === 'CONFIRMED' ? 'Last modified' : 'Invited'}: {formatDate(adminUser.lastModifiedDate || adminUser.createdDate)}
            </span>
          </div>

          {!adminUser.enabled && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
              <p className="text-xs text-red-700">⚠️ This user account is disabled</p>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-4">
          <UserX className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No administrator assigned</p>
          <p className="text-xs text-gray-400 mt-1">
            Click the menu above to invite an admin user
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminUserCard;
